import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function BlogPost() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/blog/posts/${slug}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [slug]);

  const post = data?.post;
  useSeo(post ? {
    title: post.title, description: post.excerpt, image: post.cover_image, type: "article",
    jsonLd: {
      "@context": "https://schema.org", "@type": "Article", headline: post.title,
      description: post.excerpt, image: post.cover_image || undefined,
      author: { "@type": "Organization", name: post.author || "Sitepar" },
      publisher: { "@type": "Organization", name: "Sitepar" },
      datePublished: post.published_at, dateModified: post.updated_at,
    },
  } : { title: "Blog" });

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white text-center px-6">
      <h1 className="text-2xl font-bold text-sp-navy">Article not found</h1>
      <Link to="/blog" className="mt-4 text-sp-orange">← Back to blog</Link>
    </div>
  );
  if (!post) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-white">
      <header className="h-16 bg-sp-navy flex items-center justify-between px-6"><Link to="/"><Logo dark /></Link>
        <Link to="/blog" className="text-sm text-white/70 hover:text-white flex items-center gap-1"><ArrowLeft className="h-4 w-4" />All articles</Link></header>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <button onClick={() => nav("/blog")} className="text-sm text-sp-orange mb-4">← Blog</button>
        <span className="text-xs text-sp-orange font-semibold uppercase tracking-wide">{post.category}</span>
        <h1 className="text-3xl sm:text-4xl font-bold text-sp-navy mt-2 leading-tight" data-testid="post-title">{post.title}</h1>
        <div className="flex items-center gap-3 text-sm text-slate-400 mt-4">
          <span>{post.author}</span><span>·</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{post.read_time} min read</span>
          {post.published_at && <><span>·</span><span>{new Date(post.published_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span></>}
        </div>
        {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full rounded-2xl mt-6 aspect-[16/9] object-cover" />}
        <div className="prose prose-slate max-w-none mt-8 sp-blog-content" data-testid="post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
        {post.tags?.length > 0 && <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-slate-200">
          {post.tags.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1">#{t}</span>)}</div>}
      </article>

      {data.related?.length > 0 && (
        <div className="max-w-3xl mx-auto px-6 pb-16">
          <h3 className="font-bold text-sp-navy mb-4">Related articles</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {data.related.map((r) => (
              <button key={r.id} onClick={() => nav(`/blog/${r.slug}`)} className="text-left rounded-xl border border-slate-200 p-4 hover:border-sp-orange">
                <span className="text-xs text-sp-orange">{r.category}</span>
                <div className="text-sm font-medium text-sp-navy mt-1 line-clamp-2">{r.title}</div></button>
            ))}
          </div>
        </div>
      )}
      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-400">© {new Date().getFullYear()} Sitepar</footer>
    </div>
  );
}
